// Typ: FindTangentChordMixTask (styczna → wpisany i środkowy; podwójny input)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { parseFractionLikeInput as parseFraction } from "../../../../components/inputs/FractionInput";
import CircleTangentChordTwoAnglesDiagram from "../../../../components/diagrams/CircleTangentChordTwoAnglesDiagram";

/* ===== helpers ===== */
const DEC_TOL = 0.1;
const nice = [15, 18, 20, 24, 30, 36, 40, 45, 50, 54, 60, 72, 75, 80, 90, 96, 100, 108, 120, 135, 144, 150];

function preprocessRaw(raw) {
  if (raw == null) return "";
  return String(raw).trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}
function pretty(x) {
  const s = String(x ?? "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, "").replace(".", "{,}");
}
function extractRaw(maybe) {
  if (maybe == null) return "";
  if (typeof maybe === "string" || typeof maybe === "number") return String(maybe);
  if (typeof maybe === "object") {
    if ("raw" in maybe) return String(maybe.raw ?? "");
    if ("value" in maybe) return String(maybe.value ?? "");
    if ("input" in maybe) return String(maybe.input ?? "");
  }
  return "";
}
function normalizeChange(v) {
  return typeof v === "string" ? v : extractRaw(v);
}

/* ===== generator ===== */
export function generateTask() {
  const alpha_t = nice[Math.floor(Math.random() * nice.length)];
  const alpha_w = alpha_t;
  const alpha_sr = 2 * alpha_w;
  return {
    type: "FindTangentChordMixTask",
    data: { alpha_t, alpha_w, alpha_sr },
  };
}

/* ===== treść ===== */
export function renderTask({ data }) {
  const { alpha_t, alpha_sr } = data;

  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      Na tym samym łuku <InlineMath math={"AB"} /> rozpatrujemy dwa kąty:
      wpisany <InlineMath math={"\\alpha_{w}"} /> oraz kąt między styczną w punkcie
      <InlineMath math={"\\,A"} /> a cięciwą <InlineMath math={"AB"} /> — <InlineMath math={"\\alpha_{t}"} />.
      Wiadomo, że <InlineMath math={`\\alpha_{t}=${alpha_t}^{\\circ}`} />.
      Oblicz <InlineMath math={"\\alpha_{w}"} /> oraz <InlineMath math={"\\alpha_{\\text{sr}}"} />.
      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <div style={{ alignSelf: "stretch", textAlign: "left" }}>
          Wizualizacja (dany kąt styczna–cięciwa, szukane: wpisany i środkowy):
        </div>
        <CircleTangentChordTwoAnglesDiagram
          centralDeg={alpha_sr}
          showCentral={true}
          labelSr={""}
          labelW={""}
          labelT={`${alpha_t}^{\\circ}`}
          size={260}
          circleR={108}
          padding={24}
        />
      </div>
    </div>
  );
}

/* ===== input (PODWÓJNY) ===== */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { aw: { raw: "" }, asr: { raw: "" } };
  const awRaw = extractRaw(val.aw);
  const asrRaw = extractRaw(val.asr);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
      <div style={{ fontSize: 16, marginTop: 2 }}>
        <InlineMath math={"\\alpha_{w} ="} />
      </div>
      <FractionInput
        value={awRaw}
        onChange={(v) => onChange({ ...val, aw: { raw: normalizeChange(v) } })}
        disabled={disabled}
        label={"\\alpha_{w}"}
        suffixLatex="°"
        placeholder="np. 36"
      />
      <div style={{ fontSize: 16, marginTop: 6 }}>
        <InlineMath math={"\\alpha_{\\text{sr}} ="} />
      </div>
      <FractionInput
        value={asrRaw}
        onChange={(v) => onChange({ ...val, asr: { raw: normalizeChange(v) } })}
        disabled={disabled}
        label={"\\alpha_{\\text{sr}}"}
        suffixLatex="°"
        placeholder="np. 72"
      />
    </div>
  );
}

/* ===== check ===== */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const awRaw = extractRaw(userAnswer.aw);
  const asrRaw = extractRaw(userAnswer.asr);
  if (awRaw.trim() === "" || asrRaw.trim() === "") return false;

  const aw = parseFraction(preprocessRaw(awRaw));
  const asr = parseFraction(preprocessRaw(asrRaw));
  if (!Number.isFinite(aw) || !Number.isFinite(asr)) return false;

  const okAw = Math.abs(aw - data.alpha_w) <= DEC_TOL;
  const okSr = Math.abs(asr - data.alpha_sr) <= DEC_TOL;

  return okAw && okSr;
}

/* ===== answer (poprawna) ===== */
export function renderAnswer({ data }) {
  const { alpha_w, alpha_sr } = data;
  return (
    <div style={{ marginTop: 6, lineHeight: 1.6 }}>
      <div>
        <InlineMath math={`\\alpha_{w} = ${alpha_w}^{\\circ}`} />
      </div>
      <div>
        <InlineMath math={`\\alpha_{\\text{sr}} = ${alpha_sr}^{\\circ}`} />
      </div>
    </div>
  );
}

/* ===== explanation ===== */
export function renderExplanation({ data }) {
  const { alpha_t, alpha_w, alpha_sr } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Kąt styczna–cięciwa i kąt wpisany oparte na tym samym łuku mają tę samą miarę:</div>
      <BlockMath math={"\\alpha_{t}=\\alpha_{w}"} />
      <div><strong>Krok 2.</strong> Zależność kątów: wpisany i środkowy na tym samym łuku:</div>
      <BlockMath math={"\\alpha_{\\text{sr}} = 2\\,\\alpha_{w}"} />
      <div><strong>Krok 3.</strong> Podstawiamy daną miarę kąta styczna–cięciwa:</div>
      <BlockMath math={`\\alpha_{w} = \\alpha_{t} = ${alpha_t}^{\\circ}`} />
      <div><strong>Krok 4.</strong> Obliczamy kąt środkowy:</div>
      <BlockMath math={`\\alpha_{\\text{sr}} = 2\\,\\alpha_{w} = 2\\cdot${alpha_w}^{\\circ} = ${alpha_sr}^{\\circ}`} />

      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <div style={{ alignSelf: "stretch", textAlign: "left" }}>
          Wizualizacja (oba kąty oparte na tym samym łuku + kąt środkowy):
        </div>
        <CircleTangentChordTwoAnglesDiagram
          centralDeg={alpha_sr}
          showCentral={true}
          labelSr={""}
          labelW={""}
          labelT={`${alpha_t}^{\\circ}`}
          size={280}
          circleR={108}
          padding={36}
        />
      </div>
    </div>
  );
}

/* ===== user answer preview ===== */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const awRaw = extractRaw(userAnswer.aw).trim();
  const asrRaw = extractRaw(userAnswer.asr).trim();

  if (awRaw === "" && asrRaw === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: 6, lineHeight: 1.6 }}>
      <div>
        {awRaw === "" ? (
          <i>(brak)</i>
        ) : (
          <InlineMath math={`\\displaystyle \\alpha_{w} = ${pretty(awRaw)}^{\\circ}`} />
        )}
      </div>
      <div>
        {asrRaw === "" ? (
          <i>(brak)</i>
        ) : (
          <InlineMath math={`\\displaystyle \\alpha_{\\text{sr}} = ${pretty(asrRaw)}^{\\circ}`} />
        )}
      </div>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
